/**
 * Journal Service
 * CRUD operations for journals table
 */

import { supabase } from '@/integrations/supabase/client';
import type { Journal, CreateJournalInput, UpdateJournalInput, JournalBlock } from '@/types/journal';
import { getJournalBlocks } from './journalBlockService';

/**
 * Get all journals for the current user
 */
export async function getJournals(userId: string): Promise<Journal[]> {
  const { data, error } = await supabase
    .from('journals')
    .select('*')
    .eq('user_id', userId)
    .eq('is_archived', false)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching journals:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get a single journal by ID
 */
export async function getJournal(journalId: string): Promise<Journal | null> {
  const { data, error } = await supabase
    .from('journals')
    .select('*')
    .eq('id', journalId)
    .single();

  if (error) {
    console.error('Error fetching journal:', error);
    throw error;
  }

  return data;
}

/**
 * Create a new journal
 */
export async function createJournal(
  userId: string,
  input: CreateJournalInput
): Promise<Journal> {
  const { data, error } = await supabase
    .from('journals')
    .insert({
      user_id: userId,
      name: input.name || 'Untitled Journal',
      description: input.description,
      cover_image_url: input.cover_image_url,
      ai_context: (input.ai_context || null) as any,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating journal:', error);
    throw error;
  }

  return data;
}

/**
 * Update a journal
 */
export async function updateJournal(
  journalId: string,
  updates: UpdateJournalInput
): Promise<Journal> {
  const { data, error } = await supabase
    .from('journals')
    .update(updates as any)
    .eq('id', journalId)
    .select()
    .single();

  if (error) {
    console.error('Error updating journal:', error);
    throw error;
  }

  return data;
}

/**
 * Archive a journal (soft delete)
 */
export async function archiveJournal(journalId: string): Promise<void> {
  const { error } = await supabase
    .from('journals')
    .update({ is_archived: true })
    .eq('id', journalId);

  if (error) {
    console.error('Error archiving journal:', error);
    throw error;
  }
}

/**
 * Extract storage path from a Supabase public URL
 */
function extractStoragePath(url: string, bucket: string): string | null {
  if (!url) return null;

  // Match pattern: .../storage/v1/object/public/{bucket}/{path}
  const regex = new RegExp(`/storage/v1/object/public/${bucket}/(.+)$`);
  const match = url.match(regex);
  return match ? match[1] : null;
}

/**
 * Clean up storage files associated with journal blocks
 */
async function cleanupJournalStorage(blocks: JournalBlock[]): Promise<void> {
  const pdfPaths: string[] = [];
  const imagePaths: string[] = [];

  for (const block of blocks) {
    const content = block.content as Record<string, any>;

    // Handle PDF blocks
    if (block.block_type === 'pdf' && content?.storagePath) {
      pdfPaths.push(content.storagePath);
    }

    // Handle explainer blocks with images
    if (block.block_type === 'explainer' && content?.slides) {
      for (const slide of content.slides) {
        if (slide?.imageUrl) {
          const path = extractStoragePath(slide.imageUrl, 'explainer-images');
          if (path) {
            imagePaths.push(path);
          }
        }
      }
    }
  }

  // Delete files from storage buckets
  const deletePromises: Promise<any>[] = [];

  if (pdfPaths.length > 0) {
    console.log('Deleting PDFs:', pdfPaths);
    deletePromises.push(
      supabase.storage.from('original-pdfs').remove(pdfPaths)
    );
  }

  if (imagePaths.length > 0) {
    console.log('Deleting explainer images:', imagePaths);
    deletePromises.push(
      supabase.storage.from('explainer-images').remove(imagePaths)
    );
  }

  if (deletePromises.length > 0) {
    const results = await Promise.allSettled(deletePromises);
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.warn('Failed to delete some storage files:', result.reason);
      }
    });
  }
}

/**
 * Delete a journal (hard delete)
 * Cleans up associated storage files before deleting
 */
export async function deleteJournal(journalId: string): Promise<void> {
  // Fetch blocks before deletion to clean up storage
  try {
    const blocks = await getJournalBlocks(journalId);
    await cleanupJournalStorage(blocks);
  } catch (cleanupError) {
    console.warn('Error cleaning up journal storage (continuing with delete):', cleanupError);
  }

  // Delete journal (cascades to blocks)
  const { error } = await supabase
    .from('journals')
    .delete()
    .eq('id', journalId);

  if (error) {
    console.error('Error deleting journal:', error);
    throw error;
  }
}

/**
 * Unarchive a journal
 */
export async function unarchiveJournal(journalId: string): Promise<void> {
  const { error } = await supabase
    .from('journals')
    .update({ is_archived: false })
    .eq('id', journalId);

  if (error) {
    console.error('Error unarchiving journal:', error);
    throw error;
  }
}





